import { useCallback, useEffect, useRef, useState } from "react";
import { Folder, File, ArrowUp, RefreshCw, Upload, Loader2, FolderUp, FolderDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fmtSize } from "@/lib/api";
import { b64ToBytes, saveBlob, sendFile, UPLOAD_CHUNK as CHUNK } from "@/lib/files";

const MAX = 100 * 1024 * 1024;

export const FilesPanel = ({ send, subscribe, live }) => {
  const [dir, setDir] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const downloads = useRef({});
  const mine = useRef(new Set());
  const inputRef = useRef(null);
  const folderRef = useRef(null);

  const list = useCallback((path) => {
    setBusy(true);
    send({ type: "fs_list", path: path || "", req: "list" });
  }, [send]);

  useEffect(() => subscribe((m) => {
    if (m.type === "fs_list_result") { setDir(m); setBusy(false); }
    if (m.type === "fs_error") { toast.error(m.error); setBusy(false); setProgress(""); }
    if (m.type === "fs_chunk") {
      const parts = downloads.current[m.req] || (downloads.current[m.req] = []);
      parts.push(b64ToBytes(m.data));
      setProgress(`Réception de ${m.name}… ${Math.min(100, Math.round((parts.length * CHUNK * 100) / Math.max(1, m.size)))} %`);
      if (m.last) {
        saveBlob(new Blob(parts), m.name);
        delete downloads.current[m.req];
        setProgress("");
        toast.success(`${m.name} téléchargé`);
      }
    }
    if (m.type === "fs_put_done" && mine.current.delete(m.req)) { setProgress(""); toast.success(`Fichier déposé : ${m.path}`); }
  }), [subscribe]);

  useEffect(() => { if (live && !dir) list(""); }, [live, dir, list]);

  const open = (e) => {
    const path = `${dir.path}${dir.path.endsWith(dir.sep) ? "" : dir.sep}${e.name}`;
    if (e.dir) return list(path);
    send({ type: "fs_get", path, req: `get-${Date.now()}` });
    setProgress(`Demande de ${e.name}…`);
  };

  const getDir = (e) => {
    const path = `${dir.path}${dir.path.endsWith(dir.sep) ? "" : dir.sep}${e.name}`;
    send({ type: "fs_get_dir", path, req: `getdir-${Date.now()}` });
    setProgress(`Compression de ${e.name}…`);
  };

  const upload = async (file, rel) => {
    if (!file) return;
    if (file.size > MAX) return toast.error(`Fichier trop volumineux : ${rel || file.name} (100 Mo maximum)`);
    await sendFile(send, file, rel, (p) => setProgress(`Envoi de ${rel || file.name}… ${p} %`), (req) => mine.current.add(req));
  };

  const uploadFolder = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    for (let i = 0; i < arr.length; i += 1) {
      await upload(arr[i], arr[i].webkitRelativePath);
    }
    folderRef.current.value = "";
    setProgress("");
    toast.success(`Dossier envoyé (${arr.length} fichier${arr.length > 1 ? "s" : ""})`);
  };

  return (
    <aside className="w-96 flex-shrink-0 border-l border-slate-800 bg-[#0B1120] flex flex-col" data-testid="files-panel">
      <div className="px-4 h-12 flex items-center justify-between border-b border-slate-800">
        <p className="font-mono text-[11px] tracking-widest text-slate-400">FICHIERS DU CLIENT</p>
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" disabled={!dir?.parent} onClick={() => list(dir.parent)} data-testid="files-up-btn"><ArrowUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" disabled={!live} onClick={() => list(dir?.path)} data-testid="files-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>
      <p className="px-4 py-2 font-mono text-[11px] text-slate-500 truncate border-b border-slate-800" data-testid="files-path">{dir?.path || "—"}</p>
      <div className="flex-1 overflow-y-auto" data-testid="files-list">
        {!live && <p className="p-4 text-xs text-slate-500">Disponible quand la machine est connectée.</p>}
        {busy && <Loader2 className="h-5 w-5 m-4 animate-spin text-sky-400" />}
        {!busy && dir?.entries.map((e) => (
          <div key={e.name} className="group w-full flex items-center gap-2 px-4 py-1.5 text-sm hover:bg-slate-800/70" data-testid={`file-row-${e.name}`}>
            <button onClick={() => open(e)} data-testid={`file-entry-${e.name}`} className="flex-1 flex items-center gap-2 text-left min-w-0">
              {e.dir ? <Folder className="h-4 w-4 text-sky-400 flex-shrink-0" /> : <File className="h-4 w-4 text-slate-400 flex-shrink-0" />}
              <span className="flex-1 truncate">{e.name}</span>
            </button>
            {e.dir
              ? <button onClick={() => getDir(e)} title="Recevoir le dossier (.zip)" data-testid={`file-getdir-${e.name}`} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-sky-300"><FolderDown className="h-4 w-4" /></button>
              : <span className="font-mono text-[10px] text-slate-500">{fmtSize(e.size)}</span>}
          </div>
        ))}
      </div>
      {progress && <p className="px-4 py-2 text-xs text-amber-300 border-t border-slate-800" data-testid="files-progress">{progress}</p>}
      <div className="p-3 border-t border-slate-800 space-y-2">
        <input ref={inputRef} type="file" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="files-upload-input" />
        <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={(e) => uploadFolder(e.target.files)} data-testid="files-upload-folder-input" />
        <div className="grid grid-cols-2 gap-2">
          <Button variant="secondary" disabled={!live} onClick={() => inputRef.current.click()} data-testid="files-upload-btn"><Upload className="h-4 w-4 mr-2" /> Fichier</Button>
          <Button variant="secondary" disabled={!live} onClick={() => folderRef.current.click()} data-testid="files-upload-folder-btn"><FolderUp className="h-4 w-4 mr-2" /> Dossier</Button>
        </div>
        <p className="text-[10px] text-slate-500">Cliquez un fichier pour le récupérer, ou l'icône d'un dossier pour le recevoir en .zip. 100 Mo max par fichier.</p>
      </div>
    </aside>
  );
};
